import { isMultiValueSubfield } from './multiValueSubfield';
/**
 * Separator between merged terms.
 *
 * `", "` is what NK actually catalogues. The meeting minutes left it open, so it
 * was measured on the imported corpus: of 1 600 931 `670 $b` rows, 622 492
 * occurrences are a `", "`-join of two or more known terms against 4 799 using
 * `"; "` — 99.2 % comma. Typical value: "autoritní forma, datum narození,
 * biografické údaje".
 */
export var SEPARATOR = ', ';
/**
 * True when this subfield's several values belong in ONE row with components.
 *
 * Keyed on the explicit `multiValueComponentType` declaration rather than on
 * catalog repeatability alone, for two reasons: the declaration also names the
 * component type the write path needs, and it cannot be accidentally implied by
 * a catalog gap. `MultiValueComponentTypeBindingTest` (backend) asserts that
 * anything carrying the declaration really is non-repeatable in the catalog and
 * really is bound to that property in the ontology.
 *
 * The effective backend config omits subfield `repeatable` (CZNA-873), so this
 * guard is inert on that path. The offline fallback still carries the ontology
 * value, where it prevents merging a repeatable subfield that also declares
 * `multiValueComponentType`.
 *
 * CZNA-567: the multi-ness half goes through `isMultiValueSubfield`, so a
 * config whose `multiValue` the old visual editor stripped still merges.
 *
 * Routing this one mattered, but not for the reason it first looks like. Before
 * CZNA-567 a stripped 670$b never reached here at all — the caller's own gate
 * was also `sf.multiValue`, so it took the single-value path and emitted ZERO
 * rows (the silent-loss bug). The danger was in a HALF-DONE version of this
 * change: route the outer branch, leave this one reading `multiValue === true`,
 * and a stripped 670$b would then take the row-per-term branch — several rows of
 * a MARC-non-repeatable subfield, i.e. invalid export plus a BLOCKING
 * STRUCT_SUBFIELD_REPEAT that stops the form saving at all. Blind review
 * confirmed that risk was live: un-routing just this predicate turns three
 * assertions in `multiValueCodecPackaged.test.ts` red.
 */
export var isMergedMultiValue = function (sf) {
    return isMultiValueSubfield(sf) && !!(sf === null || sf === void 0 ? void 0 : sf.multiValueComponentType) && sf.repeatable !== true;
};
/** Join the picked terms into the single subfield value. */
export var joinTerms = function (terms) { return terms.join(SEPARATOR); };
/**
 * Split on the separator only where it is NOT inside parentheses.
 *
 * A plain `split(", ")` is wrong here, and the corpus says why: real found-info
 * terms do contain parentheses — "autoritní forma (rejstříkové záhlaví)" (118
 * occurrences), "profesní údaje (obálka strana 4)" (88), "odkazy viz
 * (rejstříková záhlaví)" (41) — and a citation mis-coded into `$b` contains a
 * comma inside them: "www(Wikipedia, the free encyclopedia), cit. 17. 4. 2015".
 * Splitting that naively invents three bogus terms out of two. Banning
 * parentheses outright would instead refuse to split the 7 legitimate
 * paren-carrying terms, so depth tracking is the rule that fits both.
 *
 * Unbalanced closers (the real "odkaz viz b)" shape) clamp at depth 0 rather
 * than going negative, so they behave like plain text.
 */
var splitOutsideParens = function (value) {
    var parts = [];
    var depth = 0;
    var start = 0;
    for (var i = 0; i < value.length; i += 1) {
        var ch = value[i];
        if (ch === '(') {
            depth += 1;
        }
        else if (ch === ')') {
            depth = Math.max(0, depth - 1);
        }
        else if (depth === 0 && value.startsWith(SEPARATOR, i)) {
            parts.push(value.slice(start, i));
            i += SEPARATOR.length - 1;
            start = i + 1;
        }
    }
    parts.push(value.slice(start));
    return parts;
};
/**
 * Recover the terms from a stored value that carries no components — imported
 * corpus rows (1.6M of them) and anything saved before CZNA-449 look like this.
 *
 * The split is self-verifying: it is accepted only when re-joining the parts
 * reproduces the input byte-for-byte AND no part needed trimming. So irregular
 * spacing (`"a,  b"`, which would come back as `"a, b"`) keeps the value whole
 * instead of letting a no-op save silently reformat a record — with 1.6M rows in
 * the corpus that normalisation would be a mass rewrite, not a detail. A value
 * with no top-level separator is returned as a single term.
 */
export var splitStoredValue = function (value) {
    var trimmed = (value !== null && value !== void 0 ? value : '').trim();
    if (!trimmed)
        return [];
    var parts = splitOutsideParens(trimmed);
    if (parts.length < 2)
        return [trimmed];
    if (parts.some(function (p) { return p.length === 0 || p !== p.trim(); }))
        return [trimmed];
    if (joinTerms(parts) !== trimmed)
        return [trimmed];
    return parts;
};
